import Vue from 'vue'
import { ActionContext, ActionTree } from 'vuex'
import { StoreInterface } from '../index'
import { UserStateInterface } from './state'
import { fusionClient } from 'boot/fusionAuth'
import ClientResponse from 'app/node_modules/@fusionauth/typescript-client/build/src/ClientResponse'
import {
  RefreshResponse, ValidateResponse
} from 'app/node_modules/@fusionauth/typescript-client/build/src/FusionAuthClient'
import { QueryOptions } from 'app/node_modules/apollo-client'
import { apolloProvider } from 'boot/apollo'
import { User, UserEmail, UserGroup } from 'able-lib'
import { TOKEN_NAME, TOKEN_REFRESH_NAME } from 'src/lib/constants'
import {
  GqlUserVariable,
  QUERY_GET_NESTED_OBJECT_LISTS_FOR_USER_STORE,
  QUERY_GET_USER,
  QUERY_GET_USERS,
  QUERY_INSERT_USER_GROUP
} from 'src/lib/queries/user'

import { MutationOptions } from 'apollo-client'

interface ActionResult {
  success: boolean;
  expired?: boolean;
  data: any;
}

declare const localStorage

const actions: ActionTree<UserStateInterface, StoreInterface> = {
  async fusionAuthenticate ({ dispatch }: ActionContext<UserStateInterface, StoreInterface>): Promise<any> {
    const token = localStorage.getItem(TOKEN_NAME)
    const retrieveUser = await fusionClient.retrieveUserUsingJWT(token)
    const email = retrieveUser.response.user.email

    await dispatch('getUserInfo', { email: email })
  },
  async getUserInfo ({ commit }: ActionContext<UserStateInterface, StoreInterface>,
    { email }: { email: UserEmail }): Promise<User> {
    if (!email) return null

    const variables: GqlUserVariable = { email }

    const options: QueryOptions = {
      query: QUERY_GET_USER,
      variables,
      fetchPolicy: 'no-cache'
    }

    const { data } = await apolloProvider.defaultClient.query(options)

    if (data.users.length !== 0) {
      const user: User = data.users.find((user: User) => user.email === email)
      commit('SET_USER', user)
      return user
    } else {
      console.debug('user/actions.getUserInfo', 'Account is not registered!')
    }
  },
  async logout (): Promise<ActionResult> {
    let result: ClientResponse<void>
    try {
      result = await fusionClient.logout(false, JSON.stringify(localStorage.getItem(TOKEN_NAME)))
    } catch (e) {
      result = e
      if (process.env.DEV) console.error(e)
    }
    if (result.statusCode === 200) {
      localStorage.removeItem('user')
      localStorage.removeItem('userProfile')
      localStorage.removeItem(TOKEN_NAME)
      localStorage.removeItem(TOKEN_REFRESH_NAME)
      return {
        success: true,
        data: 'Logged Out Succesfully!'
      }
    } else {
      return {
        success: false,
        data: 'Unable to logout'
      }
    }
  },
  async fusionAuthLogout (): Promise<void> {
    const refreshToken = localStorage.getItem(TOKEN_REFRESH_NAME)
    const result = await fusionClient.logout(false, refreshToken)

    if (result.statusCode === 200) {
      localStorage.removeItem('user')
      localStorage.removeItem('userProfile')
      localStorage.removeItem(TOKEN_NAME)
      localStorage.removeItem(TOKEN_REFRESH_NAME)
    } else {
      console.error(result)
    }
  },
  async validateJWT (context: ActionContext<UserStateInterface, StoreInterface>, token): Promise<ActionResult> {
    let result: ClientResponse<ValidateResponse>
    try {
      result = await fusionClient.validateJWT(token)
    } catch (e) {
      result = e
      if (process.env.DEV) console.error(e)
    }
    if (result.statusCode === 200) {
      localStorage.removeItem('refreshToken')
      return {
        success: true,
        expired: false,
        data: result.response
      }
    } else if (result.statusCode === 401) {
      return {
        success: false,
        expired: true,
        data: 'Expired Token'
      }
    } else {
      console.log(result)
      return {
        success: false,
        expired: true,
        data: 'FusionAuth Internal Error'
      }
    }
  },
  async retrieveNewAccessToken (context: ActionContext<UserStateInterface, StoreInterface>, {
    refreshToken,
    token
  }): Promise<ActionResult> {
    let result: ClientResponse<RefreshResponse>
    try {
      result = await fusionClient.exchangeRefreshTokenForJWT({
        refreshToken,
        token
      })
    } catch (e) {
      result = e
      if (process.env.DEV) console.error(e)
    }
    if (result.statusCode === 200) {
      // @ts-ignore
      if (result.response.token != null) localStorage.setItem(TOKEN_NAME, result.response.token)
      // @ts-ignore
      if (result.response.refreshToken != null) localStorage.setItem(TOKEN_REFRESH_NAME, result.response.refreshToken)
      // @ts-ignore
      return {
        success: true,
        // @ts-ignore
        data: result.response.token
      }
    } else {
      return {
        success: false,
        data: 'Unable to retrieve new token'
      }
    }
  },
  async saveUserGroup ({ dispatch }: ActionContext<UserStateInterface, StoreInterface>, {
    messageData,
    userData
  }): Promise<void> {
    const userId = userData.id
    const user_groupObject: UserGroup = {
      group_id: messageData.messageGroups[0].group.id,
      role_id: userData.userRoles[0].role.id,
      user_id: userId
    }

    const options: MutationOptions = {
      mutation: QUERY_INSERT_USER_GROUP,
      variables: {
        user_groupObject
      }
    }

    await apolloProvider.defaultClient.mutate(options)
    await dispatch('message/getPolicies', userId, { root: true })
  },
  async getUsers ({ commit }: ActionContext<UserStateInterface, StoreInterface>): Promise<void> {
    // TODO: move this to index component
    const options: QueryOptions = {
      query: QUERY_GET_USERS,
      variables: {},
      fetchPolicy: 'network-only'
    }

    const { data } = await apolloProvider.defaultClient.query(options)

    commit('SET_USERS', data.user)
  },
  async initializeStore ({ commit }: ActionContext<UserStateInterface, StoreInterface>, userId): Promise<void> {
    const options: QueryOptions = {
      query: QUERY_GET_NESTED_OBJECT_LISTS_FOR_USER_STORE,
      variables: {
        id: userId
      },
      fetchPolicy: 'network-only'
    }

    const { data } = await apolloProvider.defaultClient.query(options)

    commit('SET_ROLES', data.roles)
    commit('SET_LOCATIONS', data.locations)
  }
}

export default actions
